import type { Aabb, District, StaticCollider, Surface, WorldProp } from "@townsy/contracts";
import { YAW_EAST, YAW_NORTH, YAW_WEST, aabb } from "../geometry";
import type { StreetInfo } from "../manifest";
import { ROAD_HALF, SIDEWALK, propColliders } from "../plots";
import {
  ARENA_AVENUE_NAME,
  ARENA_AVENUE_X,
  MARKET_STREET_Z,
  STADIUM_NORTH_FRONT_Z,
  STADIUM_SITES,
  STADIUM_SOUTH_FRONT_Z,
  STADIUM_WAY_NAME,
  STADIUM_WAY_X_MAX,
  STADIUM_WAY_X_MIN,
  STADIUM_WAY_Z,
  stadiumPlots,
} from "../venues";
import type { DistrictBuild } from "./shoppingDistrict";

/**
 * World v6, ADR-0044 decision 1 (founder 2026-10-24: "a new area attached to the town by road, all five stadiums
 * together as big landmarks visible from the overview"): the Sports Park, district `sportspark`, north of Central.
 * Still world version 6: it only ADDS (no property, billboard, interior or district of Central, Riverside or Hillcrest
 * changes; fixtures/world-v6-snapshot.json, sportsPark.test.ts).
 *
 * Layout (town metres, x east, z north):
 *  - Arena Avenue carries Aspen Avenue's line (x = 240) on north across Market Street (the Aspen corner becomes a
 *    four-way junction) through Central's closed back lot to Central's edge (z = 150) and on into the park, where it
 *    meets Stadium Way in a T-junction. 6 m road, 4 m sidewalks (x 233..247 in all), lamps by the kerb alternating
 *    sides and trees 1.5 m beyond the sidewalks, as along the avenues. Its stretch in Central (z 73..150) is Central's
 *    (arenaAvenueInCentral, tagged `central`); Central's north wall and the back lot behind Market Street's north row
 *    are open for exactly that corridor (manifest.ts colliders, ARENA_AVENUE_CORRIDOR_X).
 *  - The park spans x 36..436, z 150..345 (it touches Central's bounds on the line z = 150 and no other district).
 *    Its south wall is open for the corridor; its other walls are whole.
 *  - Stadium Way runs east-west along z = 226 from x 48 to 424 (6 m road, 4 m sidewalks); its south sidewalk stops at
 *    Arena Avenue's mouth, crossed on a crosswalk. Crosswalks: both arms of the T-junction across Stadium Way, across the
 *    avenue's mouth, and in front of each stadium door that is not at the junction.
 *  - North side, facing south (toward the town and the overview camera): Townsy FC Park, Titans Field (at the head of
 *    Arena Avenue), Sluggers Ballpark. South side, facing north: Hoopers Arena, the square, then (across Arena Avenue)
 *    Blizzard Ice Center (venues/stadiums.ts STADIUM_SITES).
 *  - The square (x 193..233, z 193..219), in the corner of Stadium Way and Arena Avenue where visitors arrive: fountain,
 *    benches and planters (Central's plaza arrangement), the spawn and "Return to plaza" point.
 *  - Closed back lots (fenced and planted by the renderer, "bound:backlot*"): everything 16 m or more behind the north
 *    side's front line (z >= 249), and south of the square's line (z <= 193) beside Arena Avenue. The stadium buildings
 *    reach into them as the attractions reach into Central's.
 * Traffic: in the whole town, Arena Avenue (both stretches) and Stadium Way are driven by the park's own loops
 * (traffic.ts townLoops: `sportspark:main`, `sportspark:side1`). No billboards (the park adds no rentable space) and
 * no plots for sale: the five stadiums are civic venues.
 */
export const SPORTS_PARK_ID = "sportspark";
export const SPORTS_PARK_NAME = "Sports Park";
/** The park's bounds: its south edge is Central's north edge (z = 150). */
export const SPORTS_PARK_BOUNDS: Aabb = aabb(36, 150, 436, 345);
/** Arena Avenue's corridor (road and both sidewalks): where Central's north wall, its back lot and the park's south wall open. */
export const ARENA_AVENUE_CORRIDOR_X = { min: ARENA_AVENUE_X - ROAD_HALF - SIDEWALK, max: ARENA_AVENUE_X + ROAD_HALF + SIDEWALK } as const;
/** Market Street's north kerb (z = 73), where Arena Avenue's road starts, and its north sidewalk's edge (z = 77). */
const MARKET_NORTH_KERB_Z = MARKET_STREET_Z + ROAD_HALF;
const MARKET_NORTH_WALK_Z = MARKET_NORTH_KERB_Z + SIDEWALK;
/** Crosswalks across a north-south road reach 6.5 m along it (manifest.ts LANE_CROSSWALK_DEPTH). */
const CROSSWALK_DEPTH = 6.5;
/** The square, in the corner of Stadium Way's south sidewalk and Arena Avenue's west sidewalk. */
export const SPORTS_PARK_SQUARE: Aabb = aabb(193, 193, ARENA_AVENUE_CORRIDOR_X.min, STADIUM_SOUTH_FRONT_Z);
/** Behind this line north of Stadium Way, and south of the square's south edge, the land is a closed back lot. */
const NORTH_BACKLOT_Z = STADIUM_NORTH_FRONT_Z + 16;
const SOUTH_BACKLOT_Z = SPORTS_PARK_SQUARE.minZ;

const north0 = STADIUM_WAY_Z + ROAD_HALF; // 229
const south0 = STADIUM_WAY_Z - ROAD_HALF; // 223
const AX = ARENA_AVENUE_X;

/** Arena Avenue's road between two z values (x 237..243). */
function avenueRoad(z0: number, z1: number): Aabb {
  return aabb(AX - ROAD_HALF, z0, AX + ROAD_HALF, z1);
}

/** Its two sidewalks between two z values, west first. */
function avenueSidewalks(z0: number, z1: number): Aabb[] {
  return [aabb(ARENA_AVENUE_CORRIDOR_X.min, z0, AX - ROAD_HALF, z1), aabb(AX + ROAD_HALF, z0, ARENA_AVENUE_CORRIDOR_X.max, z1)];
}

const lamp = (x: number, z: number, districtId: string): WorldProp => ({ kind: "lamp", position: { x, z }, rotation: 0, scale: 1, districtId });
const tree = (x: number, z: number, scale: number, districtId: string): WorldProp => ({ kind: "tree", position: { x, z }, rotation: 0, scale, districtId });
/** A kerb lamp on Arena Avenue's west (-1) or east (+1) sidewalk (0.6 m from the road, as on the avenues). */
const avenueLamp = (z: number, side: -1 | 1, districtId: string) => lamp(AX + side * (ROAD_HALF + 0.6), z, districtId);
/** Trees 1.5 m beyond Arena Avenue's sidewalks (as along the avenues: in the closed back lots or on open ground). */
const AVENUE_TREE_X = [ARENA_AVENUE_CORRIDOR_X.min - 1.5, ARENA_AVENUE_CORRIDOR_X.max + 1.5] as const;

/**
 * Arena Avenue's stretch in Central, from Market Street's north kerb to Central's edge (`edgeZ`, z = 150), tagged
 * with Central (listed after Central's other items, so every existing prop index, prop collider id and bench seat id
 * is unchanged): the road (z 73..150; at its mouth it is laid over Market Street's north sidewalk, as the avenues are
 * over Main Street's), its sidewalks from Market Street's north sidewalk on, a crosswalk across its mouth on that
 * sidewalk's line (the junction's fourth arm), kerb lamps and trees, and the lamps' and trees' colliders
 * (`prop:<i>:<kind>`, `i` continuing Central's prop list from `firstPropIndex`).
 */
export function arenaAvenueInCentral(edgeZ: number, firstPropIndex: number, districtId: string): { surfaces: Surface[]; props: WorldProp[]; colliders: StaticCollider[] } {
  const surfaces: Surface[] = [
    { kind: "road", aabb: avenueRoad(MARKET_NORTH_KERB_Z, edgeZ), districtId },
    ...avenueSidewalks(MARKET_NORTH_WALK_Z, edgeZ).map((b): Surface => ({ kind: "sidewalk", aabb: b, districtId })),
    { kind: "crosswalk", aabb: avenueRoad(MARKET_NORTH_KERB_Z, MARKET_NORTH_KERB_Z + CROSSWALK_DEPTH), districtId },
  ];
  const props: WorldProp[] = [];
  // Kerb lamps every 15 m, alternating sides (east first), clear of the mouth crosswalk; the park's carry on.
  [88, 103, 118, 133, 148].forEach((z, k) => props.push(avenueLamp(z, k % 2 === 0 ? 1 : -1, districtId)));
  for (const z of [86, 98, 110, 122, 134, 146]) for (const x of AVENUE_TREE_X) props.push(tree(x, z, 0.95, districtId));
  const colliders = props.flatMap((p, k) => propColliders(p, firstPropIndex + k, districtId));
  return { surfaces, props, colliders };
}

/** Every lamp position along one kerb of Stadium Way: every 24 m, nudged 6 m off a crosswalk or a door (`clear`). */
function kerbLamps(clear: (x: number) => boolean): number[] {
  const out: number[] = [];
  for (let x = STADIUM_WAY_X_MIN + 16; x <= STADIUM_WAY_X_MAX - 16; x += 24) {
    const at = [x, x - 6, x + 6].find(clear);
    if (at !== undefined) out.push(at);
  }
  return out;
}

export function sportsPark(): DistrictBuild {
  const id = SPORTS_PARK_ID;
  const B = SPORTS_PARK_BOUNDS;
  const plots = stadiumPlots(id);

  // ---- Surfaces ---------------------------------------------------------------------------------
  const surfaces: Surface[] = [];
  const surface = (kind: Surface["kind"], box: Aabb) => surfaces.push({ kind, aabb: box, districtId: id });
  const streetRoad = aabb(STADIUM_WAY_X_MIN, south0, STADIUM_WAY_X_MAX, north0);
  const parkAvenue = avenueRoad(B.minZ, south0);
  surface("road", streetRoad);
  surface("sidewalk", aabb(STADIUM_WAY_X_MIN, north0, STADIUM_WAY_X_MAX, STADIUM_NORTH_FRONT_Z));
  // The south sidewalk stops at Arena Avenue's mouth (the avenue's road meets Stadium Way's), as the avenues' outer
  // sidewalks stop at Market Street's kerb (world v6).
  surface("sidewalk", aabb(STADIUM_WAY_X_MIN, STADIUM_SOUTH_FRONT_Z, AX - ROAD_HALF, south0));
  surface("sidewalk", aabb(AX + ROAD_HALF, STADIUM_SOUTH_FRONT_Z, STADIUM_WAY_X_MAX, south0));
  surface("plaza", SPORTS_PARK_SQUARE);
  surface("road", parkAvenue);
  for (const b of avenueSidewalks(B.minZ, STADIUM_SOUTH_FRONT_Z)) surface("sidewalk", b);
  // Crosswalks: both arms of the T-junction across Stadium Way, across the avenue's mouth, at every other door.
  const crosswalks: Aabb[] = [
    aabb(AX - ROAD_HALF - 2 * ROAD_HALF, south0, AX - ROAD_HALF, north0),
    aabb(AX + ROAD_HALF, south0, AX + ROAD_HALF + 2 * ROAD_HALF, north0),
    avenueRoad(south0 - CROSSWALK_DEPTH, south0),
    ...STADIUM_SITES.filter((s) => s.cx !== AX).map((s) => aabb(s.cx - ROAD_HALF, south0, s.cx + ROAD_HALF, north0)),
  ];
  for (const c of crosswalks) surface("crosswalk", c);

  // ---- Props ------------------------------------------------------------------------------------
  const props: WorldProp[] = [];
  const crossingX = crosswalks.filter((c) => c.minZ >= south0 - 1e-9).map((c) => [c.minX - 1.5, c.maxX + 1.5] as const);
  const doorX = (side: "north" | "south") => STADIUM_SITES.filter((s) => s.side === side).map((s) => s.cx);
  const clearOf = (doors: readonly number[], also: (x: number) => boolean) => (x: number) => !crossingX.some(([a, b]) => x >= a && x <= b) && !doors.some((d) => Math.abs(x - d) <= 4) && also(x);
  // Kerb lamps along Stadium Way (0.6 m from the road), clear of every crosswalk, door and (south) the avenue's mouth.
  for (const x of kerbLamps(clearOf(doorX("north"), () => true))) props.push(lamp(x, north0 + 0.6, id));
  for (const x of kerbLamps(clearOf(doorX("south"), (v) => Math.abs(v - AX) > ROAD_HALF + 1))) props.push(lamp(x, south0 - 0.6, id));
  // Arena Avenue's park stretch: kerb lamps carrying on Central's (alternating, every 15 m) and trees beyond the
  // sidewalks; none west of it beside the square.
  [163, 178, 193, 208].forEach((z, k) => props.push(avenueLamp(z, k % 2 === 0 ? -1 : 1, id)));
  for (const z of [158, 170, 182]) for (const x of AVENUE_TREE_X) props.push(tree(x, z, 0.95, id));
  for (const z of [194, 206]) props.push(tree(AVENUE_TREE_X[1], z, 0.95, id));
  // The square: the fountain, four benches facing it and four planters (Central's plaza arrangement).
  const sqX = (SPORTS_PARK_SQUARE.minX + SPORTS_PARK_SQUARE.maxX) / 2; // 213
  const top = SPORTS_PARK_SQUARE.maxZ; // 219
  props.push({ kind: "fountain", position: { x: sqX, z: top - 13 }, rotation: 0, scale: 1, districtId: id });
  for (const [dx, dz] of [
    [-10, -6],
    [10, -6],
    [-10, -19],
    [10, -19],
  ] as const) {
    props.push({ kind: "bench", position: { x: sqX + dx, z: top + dz }, rotation: dx < 0 ? YAW_EAST : YAW_WEST, scale: 1, districtId: id });
    props.push({ kind: "planter", position: { x: sqX + dx * 1.3, z: top + dz + (dz < -10 ? -1.5 : 1.5) }, rotation: 0, scale: 1, districtId: id });
  }
  // Trees: closing both ends of Stadium Way, in the side yards between the north side's stadiums and past its ends,
  // and in the open ground of the south side (west of Hoopers Arena, beside the square, east of Blizzard Ice Center).
  for (const x of [STADIUM_WAY_X_MIN - 6, STADIUM_WAY_X_MAX + 6]) for (const z of [221, 226, 231]) props.push(tree(x, z, 1.1, id));
  for (const x of [52, 177, 303, 420]) for (const z of [238, 244]) props.push(tree(x, z, 1.05, id));
  for (const x of [50, 66, 82, 354, 372, 390, 408, 424]) for (const z of [200, 212]) props.push(tree(x, z, 1.05, id));
  for (const z of [199, 207, 215]) props.push(tree(186, z, 1.0, id));

  // ---- Colliders --------------------------------------------------------------------------------
  const colliders: StaticCollider[] = [];
  for (const plot of plots) colliders.push({ id: `bld:${plot.id}`, kind: "building", aabb: plot.buildBounds, districtId: id });
  props.forEach((p, i) => colliders.push(...propColliders(p, i, id)));
  const T = 2;
  const wall = (name: string, box: Aabb) => colliders.push({ id: `bound:${id}:${name}`, kind: "boundary", aabb: box, districtId: id });
  // The south wall (toward Central) is open for Arena Avenue's corridor: a west (":w") and an east (":e") piece.
  const C = ARENA_AVENUE_CORRIDOR_X;
  wall("s:w", aabb(B.minX - T, B.minZ - T, C.min, B.minZ));
  wall("s:e", aabb(C.max, B.minZ - T, B.maxX + T, B.minZ));
  wall("w", aabb(B.minX - T, B.minZ - T, B.minX, B.maxZ + T));
  wall("e", aabb(B.maxX, B.minZ - T, B.maxX + T, B.maxZ + T));
  wall("n", aabb(B.minX - T, B.maxZ, B.maxX + T, B.maxZ + T));
  const backlot = (name: string, box: Aabb) => colliders.push({ id: `bound:backlot:${id}:${name}`, kind: "boundary", aabb: box, districtId: id });
  backlot("n", aabb(B.minX, NORTH_BACKLOT_Z, B.maxX, B.maxZ));
  backlot("s:w", aabb(B.minX, B.minZ, C.min, SOUTH_BACKLOT_Z));
  backlot("s:e", aabb(C.max, B.minZ, B.maxX, SOUTH_BACKLOT_Z));

  // ---- District and streets ---------------------------------------------------------------------
  const spawnZ = top - 4; // 4 m into the square from the sidewalk (Central: z -11, 4 m in from -7)
  const anchor = (dx: number, z: number) => ({ position: { x: sqX + dx, z }, facing: YAW_NORTH });
  const district: District = {
    id,
    name: SPORTS_PARK_NAME,
    bounds: B,
    // Facing north, at Titans Field's front across Stadium Way.
    spawn: anchor(0, spawnZ),
    // Central's return anchors around the spawn, moved to the square (as in Riverside and Hillcrest).
    returnAnchors: [anchor(-4, spawnZ), anchor(4, spawnZ), anchor(0, spawnZ - 4), anchor(-6, spawnZ - 5), anchor(6, spawnZ - 5), anchor(0, spawnZ + 2.5)],
    // Looking north over Stadium Way: the three stadiums' fronts and the arenas' south boards, all five in view.
    overviewCamera: { target: { x: 236, y: 8, z: 238 }, alpha: -Math.PI / 2, beta: 1.0, radius: 265 },
    square: SPORTS_PARK_SQUARE,
    gates: [],
  };
  const streets: StreetInfo[] = [
    { id: "stadium_way", name: STADIUM_WAY_NAME, kind: "street", roads: [streetRoad], from: { x: streetRoad.minX, z: STADIUM_WAY_Z }, to: { x: streetRoad.maxX, z: STADIUM_WAY_Z } },
    // From Market Street: its stretch in Central (arenaAvenueInCentral) and the park's, one street.
    { id: "arena_avenue", name: ARENA_AVENUE_NAME, kind: "avenue", roads: [avenueRoad(MARKET_NORTH_KERB_Z, B.minZ), parkAvenue], from: { x: AX, z: MARKET_NORTH_KERB_Z }, to: { x: AX, z: south0 } },
  ];
  return { district, plots, billboards: [], surfaces, props, colliders, streets };
}
